import { Button, Select, Tabs, TextInput } from '@mantine/core';
import {
  IconBooks,
  IconCategory,
  IconCircleCheck,
  IconCircleOff,
  IconEdit,
  IconPlus,
  IconSearch,
} from '@tabler/icons-react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { EmptyState } from '../../EmptyState.js';
import { useFitColumns } from '../../fit-columns.js';
import { PageHeader } from '../../PageHeader.js';
import { Panel } from '../../Panel.js';
import { StatTile } from '../../StatTile.js';
import { StatusBadge } from '../../StatusBadge.js';
import table from '../materials/Materials.module.css';
import { errorCode } from '../personnel/source.js';
import lib from '../pms-library/PmsLibrary.module.css';
import {
  LoadError,
  SortableTh,
  type SortState,
  TablePager,
  TableSkeleton,
} from '../purchasing/table.js';
import { CategoryDrawer, TemplateDetailDrawer, TemplateEditDrawer } from './DrillDrawers.js';
import { DrillImportTab } from './DrillImport.js';
import classes from './Drills.module.css';
import { frequencyLabel } from './labels.js';
import type { DrillLibrarySource } from './source.js';
import type { DrillCategory, DrillLibrary, DrillTemplateDetail } from './types.js';
import {
  categoryTree,
  filterTemplates,
  frequencyOrder,
  PAGE_SIZE,
  pageOf,
  pagesOf,
  type StatusFilter,
  sortRows,
} from './view.js';

export interface DrillLibraryScreenProps {
  source: DrillLibrarySource;
}

type Tab = 'library' | 'categories' | 'importExport';

type State =
  | { kind: 'loading' }
  | { kind: 'error'; code: string }
  | { kind: 'ready'; library: DrillLibrary };

/**
 * Operations ▸ Drills ▸ Library (ADR 0128 Faz 1; ortağın belgesi §3, §4): şirketin tatbikat
 * şablonları kategori ağacıyla. Ofiste şablon ve kategori yazılır, Excel içe/dışa aktarılır;
 * gemide aynı ekran salt okunurdur (kütüphane buluttan iner).
 */
export function DrillLibraryScreen({ source }: DrillLibraryScreenProps) {
  const { t } = useTranslation();
  const [state, setState] = useState<State>({ kind: 'loading' });
  const [tab, setTab] = useState<Tab>('library');
  const [detailId, setDetailId] = useState<string | null>(null);
  const [editing, setEditing] = useState<{
    detail: DrillTemplateDetail | null;
    categoryId: string | null;
  } | null>(null);
  const [category, setCategory] = useState<{ category: DrillCategory | null } | null>(null);
  const [selected, setSelected] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      setState({ kind: 'ready', library: await source.load() });
    } catch (error) {
      setState({ kind: 'error', code: errorCode(error) });
    }
  }, [source]);
  useEffect(() => {
    void load();
  }, [load]);

  const office = source.scope === 'office';
  const library = state.kind === 'ready' ? state.library : null;
  const canCreate = office && (library?.can?.create ?? false) && Boolean(source.saveTemplate);
  const canEdit = office && (library?.can?.edit ?? false) && Boolean(source.saveTemplate);

  const header = (
    <PageHeader
      title={t('drills.library.title')}
      description={t(office ? 'drills.library.description' : 'drills.library.shipDescription')}
      actions={
        canCreate ? (
          <Button
            leftSection={<IconPlus size={16} aria-hidden />}
            onClick={() => setEditing({ detail: null, categoryId: selected })}
          >
            {t('drills.actions.newTemplate')}
          </Button>
        ) : undefined
      }
    />
  );

  if (state.kind === 'loading') {
    return (
      <div className={lib.screen}>
        {header}
        <TableSkeleton columns={6} label={t('drills.library.title')} />
      </div>
    );
  }
  if (state.kind === 'error') {
    return (
      <div className={lib.screen}>
        {header}
        <Panel>
          <LoadError
            code={state.code}
            title={t('drills.library.error')}
            onRetry={() => {
              setState({ kind: 'loading' });
              void load();
            }}
          />
        </Panel>
      </div>
    );
  }

  const data = state.library;
  const active = data.templates.filter((tpl) => tpl.active).length;
  const topLevel = data.categories.filter((c) => c.parentId === null).length;
  const showTabs = office;

  const libraryTab = (
    <LibraryTab
      library={data}
      selected={selected}
      onSelect={setSelected}
      canEdit={canEdit}
      onOpen={setDetailId}
      onEdit={async (id) => setEditing({ detail: await source.template(id), categoryId: null })}
    />
  );

  return (
    <div className={lib.screen}>
      {header}
      <div className={lib.stats}>
        <StatTile
          icon={<IconBooks size={20} />}
          label={t('drills.stats.templates')}
          value={data.templates.length}
        />
        <StatTile
          icon={<IconCategory size={20} />}
          label={t('drills.stats.categories')}
          value={topLevel}
        />
        <StatTile
          icon={<IconCircleCheck size={20} />}
          label={t('drills.stats.active')}
          value={active}
          tone="success"
        />
        <StatTile
          icon={<IconCircleOff size={20} />}
          label={t('drills.stats.inactive')}
          value={data.templates.length - active}
        />
      </div>
      {showTabs ? (
        <Tabs
          value={tab}
          onChange={(value) => setTab((value as Tab | null) ?? 'library')}
          className={lib.tabs}
        >
          <Tabs.List>
            <Tabs.Tab value="library">{t('drills.tabs.library')}</Tabs.Tab>
            <Tabs.Tab value="categories">{t('drills.tabs.categories')}</Tabs.Tab>
            <Tabs.Tab value="importExport">{t('drills.tabs.importExport')}</Tabs.Tab>
          </Tabs.List>
          <Tabs.Panel value="library" className={lib.tabPanel}>
            {libraryTab}
          </Tabs.Panel>
          <Tabs.Panel value="categories" className={lib.tabPanel}>
            <CategoriesTab
              library={data}
              canCreate={canCreate && Boolean(source.saveCategory)}
              canEdit={canEdit && Boolean(source.saveCategory)}
              onAdd={() => setCategory({ category: null })}
              onEdit={(c) => setCategory({ category: c })}
            />
          </Tabs.Panel>
          <Tabs.Panel value="importExport" className={lib.tabPanel}>
            <DrillImportTab
              source={source}
              canImport={canCreate}
              canExport={data.can?.export ?? false}
              onImported={load}
            />
          </Tabs.Panel>
        </Tabs>
      ) : (
        libraryTab
      )}
      <TemplateDetailDrawer
        id={detailId}
        library={data}
        source={source}
        canEdit={canEdit}
        onClose={() => setDetailId(null)}
        onEdit={(detail) => {
          setDetailId(null);
          setEditing({ detail, categoryId: null });
        }}
      />
      <TemplateEditDrawer
        target={editing}
        library={data}
        source={source}
        onClose={() => setEditing(null)}
        onSaved={async (saved) => {
          setEditing(null);
          await load();
          setDetailId(saved.id);
        }}
      />
      <CategoryDrawer
        target={category}
        library={data}
        source={source}
        onClose={() => setCategory(null)}
        onSaved={async () => {
          setCategory(null);
          await load();
        }}
      />
    </div>
  );
}

type SortKey = 'code' | 'name' | 'frequency' | 'version';

export function LibraryTab({
  library,
  selected,
  onSelect,
  canEdit,
  onOpen,
  onEdit,
}: {
  library: DrillLibrary;
  selected: string | null;
  onSelect: (id: string | null) => void;
  canEdit: boolean;
  onOpen: (id: string) => void;
  onEdit: (id: string) => Promise<void>;
}) {
  const { t } = useTranslation();
  const [query, setQuery] = useState('');
  const [status, setStatus] = useState<StatusFilter>('all');
  const [sort, setSort] = useState<SortState<SortKey>>({ key: 'code', dir: 'asc' });
  const [page, setPage] = useState(1);
  const fit = useFitColumns<HTMLElement>();
  const tree = useMemo(() => categoryTree(library.categories), [library.categories]);
  const names = useMemo(
    () => new Map(library.categories.map((c) => [c.id, c.name])),
    [library.categories],
  );
  const rows = useMemo(() => {
    const filtered = filterTemplates(library, { categoryId: selected, query, status });
    return sortRows(
      filtered,
      (row) =>
        sort.key === 'frequency'
          ? frequencyOrder(row.frequency)
          : sort.key === 'version'
            ? row.version
            : sort.key === 'name'
              ? row.name
              : row.code,
      sort.dir,
    );
  }, [library, selected, query, status, sort]);
  const pages = pagesOf(rows.length);
  const current = Math.min(page, pages);
  const shown = pageOf(rows, current);
  const k = (key: string) => t(`drills.columns.${key}`);
  const choose = (id: string | null) => {
    onSelect(id);
    setPage(1);
  };
  const summary =
    rows.length === 0
      ? ''
      : t('drills.showing', {
          from: (current - 1) * PAGE_SIZE + 1,
          to: Math.min(rows.length, current * PAGE_SIZE),
          total: rows.length,
        });

  return (
    <div className={lib.split}>
      <Panel title={t('drills.library.categories')} flush>
        <nav className={lib.systemList} aria-label={t('drills.library.categories')}>
          <button
            type="button"
            className={`${lib.systemButton} ${classes.treeButton}`}
            data-active={selected === null || undefined}
            aria-pressed={selected === null}
            onClick={() => choose(null)}
          >
            <span className={lib.systemCode}>—</span>
            <span className={lib.systemName}>{t('drills.filters.allCategories')}</span>
            <span className={lib.count}>{library.templates.length}</span>
          </button>
          {tree.map(({ category, children }) => (
            <div key={category.id}>
              <button
                type="button"
                className={`${lib.systemButton} ${classes.treeButton}`}
                data-active={selected === category.id || undefined}
                aria-pressed={selected === category.id}
                onClick={() => choose(category.id)}
                title={category.name}
              >
                <span className={`${lib.systemCode} ${classes.code}`}>{category.code}</span>
                <span
                  className={`${lib.systemName} ${category.active ? '' : classes.inactiveName}`}
                >
                  {category.name}
                </span>
                <span className={lib.count}>{category.templateCount}</span>
              </button>
              {children.map((child) => (
                <button
                  key={child.id}
                  type="button"
                  className={`${lib.systemButton} ${classes.treeButton} ${classes.subButton}`}
                  data-active={selected === child.id || undefined}
                  aria-pressed={selected === child.id}
                  onClick={() => choose(child.id)}
                  title={child.name}
                >
                  <span className={`${lib.systemCode} ${classes.code}`}>
                    {child.code.split('.').at(-1)}
                  </span>
                  <span className={`${lib.systemName} ${child.active ? '' : classes.inactiveName}`}>
                    {child.name}
                  </span>
                  <span className={lib.count}>{child.templateCount}</span>
                </button>
              ))}
            </div>
          ))}
        </nav>
      </Panel>
      <Panel title={t('drills.library.templates')} flush>
        <div className={lib.toolbar}>
          <TextInput
            className={lib.search}
            aria-label={t('drills.filters.search')}
            placeholder={t('drills.filters.search')}
            leftSection={<IconSearch size={16} aria-hidden />}
            value={query}
            onChange={(event) => {
              setQuery(event.currentTarget.value);
              setPage(1);
            }}
          />
          <Select
            className={lib.filter}
            aria-label={t('drills.filters.status')}
            data={(['all', 'active', 'inactive'] as const).map((value) => ({
              value,
              label: t(`drills.filters.statuses.${value}`),
            }))}
            value={status}
            allowDeselect={false}
            onChange={(value) => {
              setStatus((value as StatusFilter | null) ?? 'all');
              setPage(1);
            }}
          />
        </div>
        {shown.length === 0 ? (
          <EmptyState
            title={t(
              library.templates.length === 0 ? 'drills.empty.library' : 'drills.empty.filtered',
            )}
          />
        ) : (
          <section
            ref={fit}
            className={table.tableWrap}
            aria-label={t('drills.library.templateList')}
            // biome-ignore lint/a11y/noNoninteractiveTabindex: kayan tablo klavyeyle kaydırılır
            tabIndex={0}
          >
            <table
              className={`${table.table} ${classes.templatesTable}`}
              data-actions={canEdit || undefined}
            >
              <thead>
                <tr>
                  <SortableTh
                    column="code"
                    label={k('code')}
                    sort={sort}
                    onSort={setSort}
                    className={classes.colCode ?? ''}
                  />
                  <SortableTh column="name" label={k('name')} sort={sort} onSort={setSort} />
                  <th scope="col" className={classes.colCategory} data-drop={2}>
                    {k('category')}
                  </th>
                  <SortableTh
                    column="frequency"
                    label={k('frequency')}
                    sort={sort}
                    onSort={setSort}
                    className={classes.colFrequency ?? ''}
                    drop={1}
                  />
                  <SortableTh
                    column="version"
                    label={k('version')}
                    sort={sort}
                    onSort={setSort}
                    numeric
                    className={classes.colVersion ?? ''}
                    drop={3}
                  />
                  <th scope="col" className={classes.colStatus}>
                    {k('status')}
                  </th>
                  {canEdit ? (
                    <th scope="col" className={lib.colActions}>
                      <span className={lib.visuallyHidden}>{k('actions')}</span>
                    </th>
                  ) : null}
                </tr>
              </thead>
              <tbody>
                {shown.map((row) => (
                  <tr key={row.id}>
                    <td>{row.code}</td>
                    <td title={row.name}>
                      <button
                        type="button"
                        className={classes.nameButton}
                        onClick={() => onOpen(row.id)}
                      >
                        {row.name}
                      </button>
                    </td>
                    <td data-drop={2} title={names.get(row.subcategoryId ?? row.categoryId)}>
                      {names.get(row.subcategoryId ?? row.categoryId) ?? '—'}
                    </td>
                    <td data-drop={1}>{frequencyLabel(t, row.frequency)}</td>
                    <td data-numeric data-drop={3}>
                      v{row.version}
                    </td>
                    <td>
                      <StatusBadge tone={row.active ? 'success' : 'neutral'}>
                        {t(row.active ? 'drills.status.active' : 'drills.status.inactive')}
                      </StatusBadge>
                    </td>
                    {canEdit ? (
                      <td className={lib.colActions}>
                        <Button
                          size="compact-xs"
                          variant="subtle"
                          aria-label={`${t('drills.actions.edit')} ${row.code}`}
                          onClick={() => void onEdit(row.id)}
                        >
                          <IconEdit size={16} aria-hidden />
                        </Button>
                      </td>
                    ) : null}
                  </tr>
                ))}
              </tbody>
            </table>
          </section>
        )}
        <TablePager
          page={current}
          pages={pages}
          summary={summary}
          label={t('drills.library.templates')}
          onPage={setPage}
        />
      </Panel>
    </div>
  );
}

export function CategoriesTab({
  library,
  canCreate,
  canEdit,
  onAdd,
  onEdit,
}: {
  library: DrillLibrary;
  canCreate: boolean;
  canEdit: boolean;
  onAdd: () => void;
  onEdit: (category: DrillCategory) => void;
}) {
  const { t } = useTranslation();
  const tree = categoryTree(library.categories);
  const k = (key: string) => t(`drills.columns.${key}`);
  return (
    <Panel
      title={t('drills.library.categories')}
      flush
      extra={
        canCreate ? (
          <Button
            size="compact-sm"
            variant="subtle"
            leftSection={<IconPlus size={14} aria-hidden />}
            onClick={onAdd}
          >
            {t('drills.actions.addCategory')}
          </Button>
        ) : undefined
      }
    >
      {tree.length === 0 ? (
        <EmptyState title={t('drills.empty.categories')} />
      ) : (
        <section
          className={table.tableWrap}
          aria-label={t('drills.library.categoryList')}
          // biome-ignore lint/a11y/noNoninteractiveTabindex: kayan tablo klavyeyle kaydırılır
          tabIndex={0}
        >
          <table className={table.table}>
            <thead>
              <tr>
                <th scope="col" className={classes.colCode}>
                  {k('code')}
                </th>
                <th scope="col">{k('categoryName')}</th>
                <th scope="col" className={classes.colVersion} data-numeric>
                  {k('templates')}
                </th>
                <th scope="col" className={classes.colStatus}>
                  {k('status')}
                </th>
                {canEdit ? (
                  <th scope="col" className={lib.colActions}>
                    <span className={lib.visuallyHidden}>{k('actions')}</span>
                  </th>
                ) : null}
              </tr>
            </thead>
            <tbody>
              {tree.flatMap(({ category, children }) =>
                [category, ...children].map((c) => (
                  <tr key={c.id}>
                    <td>{c.code}</td>
                    <td title={c.name}>
                      {c.parentId ? <span className={lib.muted}>└ </span> : null}
                      {c.name}
                    </td>
                    <td data-numeric>{c.templateCount}</td>
                    <td>
                      <StatusBadge tone={c.active ? 'success' : 'neutral'}>
                        {t(c.active ? 'drills.status.active' : 'drills.status.inactive')}
                      </StatusBadge>
                    </td>
                    {canEdit ? (
                      <td className={lib.colActions}>
                        <Button
                          size="compact-xs"
                          variant="subtle"
                          aria-label={`${t('drills.actions.edit')} ${c.code}`}
                          onClick={() => onEdit(c)}
                        >
                          <IconEdit size={16} aria-hidden />
                        </Button>
                      </td>
                    ) : null}
                  </tr>
                )),
              )}
            </tbody>
          </table>
        </section>
      )}
    </Panel>
  );
}
